import { useState } from "react";
import {
  Sparkles,
  SlidersHorizontal,
  X,
  Map,
  CalendarDays,
  User,
  MapPinOff,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { ProjectPlan, ProjectTag, RecordCreator } from "@/types/api";
import { isPlanArchived } from "@/lib/records/plan-utils";
import { formatDateRangeLabel } from "@/lib/records/date-range-chips";
import { creatorDisplayName } from "@/components/common/creator-filter/creator-display";
import { RecordsDrawer } from "@/components/common/records-drawer/records-drawer";

interface RecordsToolbarProps {
  tags: ProjectTag[];
  plans: ProjectPlan[];
  searchQuery: string | null;
  /** Omitted when search is unavailable (offline) — the search input is then
   * hidden. AI search is server-only. See offline-availability.md. */
  onSearchChange?: (value: string | null) => void;
  selectedTags: string[];
  onTagToggle: (tagId: string) => void;
  showUntagged: boolean;
  onUntaggedToggle: () => void;
  selectedPlanIds: string[];
  onPlanToggle: (planId: string) => void;
  dateFrom: string | null;
  dateTo: string | null;
  onDateFromChange: (value: string | null) => void;
  onDateToChange: (value: string | null) => void;
  creators: RecordCreator[];
  selectedCreatorIds: string[];
  onCreatorToggle: (creatorId: string) => void;
  onClearAll: () => void;
  /**
   * When true, suppress the plan chip in the active-filter strip and hide the
   * plan section inside the drawer. Used by single-plan contexts such as the
   * plan viewer, where the plan is already fixed.
   */
  hidePlanFilter?: boolean;
  /**
   * Active value of the unpinned filter — see records-toolbar.spec.md. The
   * toolbar offers no in-place entry; it only surfaces an existing `false`
   * value as a dismissible chip (seeded by the unpinned-photos-widget).
   */
  hasPin?: boolean | null;
  /** Required (alongside `hasPin === false`) for the Unpinned chip to render. */
  onClearHasPin?: () => void;
  /**
   * Render at every width instead of phones only. The Photos page sets this
   * when the record side panel leaves no room for the desktop filter sidebar,
   * so search and the filter sheet move here. See [[records-view.spec]] US-006.
   */
  alwaysVisible?: boolean;
}

export function RecordsToolbar(props: RecordsToolbarProps) {
  const {
    tags,
    plans,
    searchQuery,
    onSearchChange,
    selectedTags,
    onTagToggle,
    showUntagged,
    onUntaggedToggle,
    selectedPlanIds,
    onPlanToggle,
    dateFrom,
    dateTo,
    onDateFromChange,
    onDateToChange,
    creators,
    selectedCreatorIds,
    onCreatorToggle,
    onClearAll,
    hidePlanFilter = false,
    hasPin = null,
    onClearHasPin,
    alwaysVisible = false,
  } = props;
  const showUnpinnedChip = hasPin === false && !!onClearHasPin;

  const [searchInput, setSearchInput] = useState(searchQuery ?? "");
  const [searchInputSource, setSearchInputSource] = useState(searchQuery);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Mirror externally controlled `searchQuery` into the local input via
  // a render-time state adjustment instead of an effect.
  if (searchInputSource !== searchQuery) {
    setSearchInputSource(searchQuery);
    setSearchInput(searchQuery ?? "");
  }

  const hasDateFilter = !!dateFrom || !!dateTo;
  // Shown whenever the project has at least one creator. Hidden only on an
  // empty creators list — e.g. the public share page, which intentionally
  // passes no creators (no public endpoint to list contributors).
  const showCreatorFilter = creators.length > 0;
  // Hidden when the project has one or fewer plans — nothing to filter by.
  const showPlanFilter = !hidePlanFilter && plans.length > 1;
  const visibleSelectedPlanIds = showPlanFilter ? selectedPlanIds : [];
  const filterCount =
    selectedTags.length +
    visibleSelectedPlanIds.length +
    (hasDateFilter ? 1 : 0) +
    (showUntagged ? 1 : 0) +
    selectedCreatorIds.length +
    (showUnpinnedChip ? 1 : 0);
  const hasActiveFilters = filterCount > 0 || !!searchQuery;

  // When hiding the plan filter, plans don't count toward the "nothing to
  // filter" null-render check — the toolbar should still render for tags /
  // date range even if the project has no plans or the plans list is hidden.
  // Also keep the toolbar mounted when the unpinned chip is showing — without
  // this, a sparse project (no tags, no plans, ≤1 creator) entered from the
  // unpinned-photos-widget would render no toolbar, trapping the user with
  // an invisible active filter.
  // Plans contribute to the null-render decision only when the picker is
  // actually visible: i.e. not hidden by `hidePlanFilter` (plan-viewer scope)
  // and the project has more than one plan to choose from (matches the
  // sidebar/drawer plan-section visibility gate — see [[plan-filter.spec]]).
  const effectivePlanCount =
    hidePlanFilter || plans.length <= 1 ? 0 : plans.length;
  // Keep the toolbar mounted whenever search is available (`onSearchChange` is
  // set — i.e. online). Otherwise a sparse project (no tags, ≤1 plan, no
  // creators) would render no toolbar and hence no search box on mobile, even
  // though search works — a real gap for mobile-first jobsite users and on the
  // public share page. Only collapse to null when there is genuinely nothing to
  // show: no search AND nothing to filter. The desktop sidebar has no such gate
  // (it always shows search), so this aligns the two breakpoints.
  if (
    !onSearchChange &&
    tags.length === 0 &&
    effectivePlanCount === 0 &&
    !showCreatorFilter &&
    !showUnpinnedChip
  ) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-2 px-3 py-2",
        !alwaysVisible && "md:hidden",
      )}
    >
      {/* Search row: the pill takes the full width. The photos page's grid/list
          toggle is in its title row above, not here. */}
      <div className="flex items-center gap-2">
        <div
          data-testid="records-toolbar-pill"
          className={cn(
            "flex min-w-0 flex-1 items-center bg-background transition-all",
            // The pill chrome wraps the search input. Offline (search hidden)
            // the filter button styles itself as a standalone control instead.
            onSearchChange &&
              (searchQuery
                ? "rounded-lg border-2 border-tertiary"
                : "rounded-lg border border-border"),
          )}
        >
          {/* AI search is server-only — hidden offline (onSearchChange omitted). */}
          {onSearchChange && (
            <form
              className="flex-1 relative"
              onSubmit={(e) => {
                e.preventDefault();
                const trimmed = searchInput.trim();
                onSearchChange(trimmed || null);
              }}
            >
              <Sparkles
                className={cn(
                  "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4",
                  searchQuery ? "text-tertiary-ink" : "text-muted-foreground",
                )}
              />
              <Input
                type="search"
                enterKeyHint="search"
                placeholder="AI search photos…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="border-0 shadow-none focus-visible:ring-0 pl-9 pr-8 h-9 text-base rounded-none"
              />
              {(searchInput || searchQuery) && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setSearchInput("");
                    onSearchChange(null);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </form>
          )}
          <button
            type="button"
            aria-label="Open filters"
            onClick={() => setDrawerOpen(true)}
            className={cn(
              "relative shrink-0 flex items-center justify-center h-9",
              onSearchChange
                ? "w-10 border-l border-border"
                : "gap-2 rounded-lg border border-border px-3 text-sm",
              filterCount > 0 ? "text-tertiary-ink" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <SlidersHorizontal className="h-4 w-4" />
            {!onSearchChange && <span>Filters</span>}
            {filterCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 flex items-center justify-center text-[10px] font-bold rounded-full bg-tertiary text-tertiary-foreground">
                {filterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {(selectedTags.length > 0 ||
        showUntagged ||
        visibleSelectedPlanIds.length > 0 ||
        hasDateFilter ||
        selectedCreatorIds.length > 0 ||
        showUnpinnedChip) && (
        <div className="flex flex-wrap gap-2 pb-1">
          {selectedTags.map((tagId) => {
            const tag = tags.find((t) => t.id === tagId);
            if (!tag) return null;
            return (
              <Badge
                key={tagId}
                variant="secondary"
                className="shrink-0 gap-1 cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`Remove ${tag.name} filter`}
                onClick={() => onTagToggle(tagId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onTagToggle(tagId);
                  }
                }}
              >
                {tag.name}
                <X className="h-3 w-3" />
              </Badge>
            );
          })}
          {showUntagged && (
            <Badge
              variant="secondary"
              className="shrink-0 gap-1 cursor-pointer"
              role="button"
              tabIndex={0}
              aria-label="Remove untagged filter"
              onClick={onUntaggedToggle}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onUntaggedToggle();
                }
              }}
            >
              Untagged
              <X className="h-3 w-3" />
            </Badge>
          )}
          {visibleSelectedPlanIds.map((planId) => {
            const plan = plans.find((p) => p.id === planId);
            // Fall back to the raw id when the selected plan isn't in the
            // current list (data drift) — mirrors the creator-chip pattern.
            const name = plan ? plan.name : planId;
            const archived = plan ? isPlanArchived(plan) : false;
            return (
              <Badge
                key={planId}
                variant="secondary"
                className="shrink-0 gap-1 cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`Remove ${name} plan filter`}
                onClick={() => onPlanToggle(planId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onPlanToggle(planId);
                  }
                }}
              >
                <Map className="h-3 w-3" />
                {name}
                {archived ? " (Archived)" : ""}
                <X className="h-3 w-3" />
              </Badge>
            );
          })}
          {hasDateFilter && (
            <Badge
              variant="secondary"
              className="shrink-0 gap-1 cursor-pointer"
              role="button"
              tabIndex={0}
              aria-label="Remove date range filter"
              onClick={() => {
                onDateFromChange(null);
                onDateToChange(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onDateFromChange(null);
                  onDateToChange(null);
                }
              }}
            >
              <CalendarDays className="h-3 w-3" />
              {formatDateRangeLabel(dateFrom, dateTo)}
              <X className="h-3 w-3" />
            </Badge>
          )}
          {showUnpinnedChip && (
            <Badge
              variant="secondary"
              className="shrink-0 gap-1 cursor-pointer"
              role="button"
              tabIndex={0}
              aria-label="Remove unpinned filter"
              onClick={onClearHasPin}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClearHasPin?.();
                }
              }}
            >
              <MapPinOff className="h-3 w-3" />
              Unpinned only
              <X className="h-3 w-3" />
            </Badge>
          )}
          {selectedCreatorIds.map((creatorId) => {
            const creator = creators.find((c) => c.id === creatorId);
            if (!creator) return null;
            const name = creatorDisplayName(creator);
            return (
              <Badge
                key={creatorId}
                variant="secondary"
                className="shrink-0 gap-1 cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`Remove ${name} filter`}
                onClick={() => onCreatorToggle(creatorId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onCreatorToggle(creatorId);
                  }
                }}
              >
                <User className="h-3 w-3" />
                {name}
                <X className="h-3 w-3" />
              </Badge>
            );
          })}
        </div>
      )}

      <RecordsDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        tags={tags}
        plans={hidePlanFilter ? undefined : plans}
        selectedTags={selectedTags}
        onTagToggle={onTagToggle}
        showUntagged={showUntagged}
        onUntaggedToggle={onUntaggedToggle}
        selectedPlanIds={hidePlanFilter ? [] : selectedPlanIds}
        onPlanToggle={hidePlanFilter ? undefined : onPlanToggle}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={onDateFromChange}
        onDateToChange={onDateToChange}
        creators={creators}
        selectedCreatorIds={selectedCreatorIds}
        onCreatorToggle={onCreatorToggle}
        onClearAll={onClearAll}
        hasActiveFilters={hasActiveFilters}
      />
    </div>
  );
}
